import { State, Action, StateContext, Selector, NgxsOnInit } from '@ngxs/store';
import { Observable, throwError } from 'rxjs';
import { map, catchError } from 'rxjs/operators';
import * as fromNotificationsAction from './notifications.action';
import { NotificationService } from '@services/notification.service';
import { inject, Injectable } from '@angular/core';
import { INotification } from '../interfaces/notification';
import { environment } from '@env/environment';
import { SetFormPristine } from '@ngxs/form-plugin';
import { Navigate } from '@ngxs/router-plugin';
import { toastr } from '@store/operators/toastr.operator';
import { ActionType } from '@enums/action-type';
import { StateModel } from '@interfaces/state-model';

@State<StateModel<INotification>>({
  name: 'notifications',
  defaults: {
    models: undefined,
    pages: 0,
    currentPage: 0,
      selectedModel: {
      model: {} as INotification,
      dirty: false,
      status: '',
      errors: []
    },
    error: undefined
  }
})

@Injectable()
export class NotificationsState implements NgxsOnInit {
  private notificationService = inject(NotificationService);

  @Selector()
  static getNotifications(state: StateModel<INotification>): INotification[] | undefined {
    return state?.models;
  }

  @Selector()
  static getError(state: StateModel<INotification>): string | undefined {
    return state?.error;
  }

  ngxsOnInit({ setState, getState }: StateContext<StateModel<INotification>>): void {
    if (getState()) {
      return;
    }

    setState({
      models: undefined,
      pages: 0,
      currentPage: 0,
      selectedModel: undefined,
      error: undefined
    });
  }

  @Action([fromNotificationsAction.GetNotifications])
  getNotifications(
    { dispatch }: StateContext<StateModel<INotification>>
  ): any {
    return this.notificationService.getNotifications().pipe(
      map((response: INotification[]) => dispatch([
        new fromNotificationsAction.GetNotificationsSuccess(response)
      ])),
      catchError(error => dispatch(new fromNotificationsAction.GetNotificationsFail(error)))
    );
  }

  @Action(fromNotificationsAction.GetNotificationsSuccess)
  getNotificationsSuccess(
    { patchState }: StateContext<StateModel<INotification>>,
    { payload }: fromNotificationsAction.GetNotificationsSuccess
  ): void {
    patchState({
      models: payload
    });
  }

  @Action(fromNotificationsAction.GetNotificationsFail)
  getNotificationsFail(
    { patchState }: StateContext<StateModel<INotification>>,
    { payload }: fromNotificationsAction.GetNotificationsFail
  ): Observable<never> {
    patchState({
      models: undefined,
      error: payload
    });

    return throwError(() => new Error(payload.message));
  }

  @Action(fromNotificationsAction.SaveNotification)
  saveNotification(
    { dispatch }: StateContext<StateModel<INotification>>,
    { payload, inDialog }: fromNotificationsAction.SaveNotification
  ): Observable<any> {
    const action = (payload.id ? ActionType.update : ActionType.create);

    return this.notificationService.saveNotification(payload).pipe(
      map(() => dispatch(new fromNotificationsAction.SaveNotificationSuccess(1, environment.itemsPerPage, inDialog))),
      catchError(error => dispatch(new fromNotificationsAction.SaveNotificationFail(error))),
      toastr(dispatch, action, 'Notification', payload.title));
  }

  @Action(fromNotificationsAction.SaveNotificationSuccess)
  saveNotificationSuccess(
    { dispatch }: StateContext<StateModel<INotification>>,
    { inDialog }: fromNotificationsAction.SaveNotificationSuccess
  ): void {
    dispatch(new SetFormPristine('notifications.selectedModel'));

    if (!inDialog) {
      dispatch(new Navigate(['/admin/managenotifications']));
    } else {
      dispatch(new fromNotificationsAction.GetNotifications());
    }
  }

  @Action(fromNotificationsAction.SaveNotificationFail)
  saveNotificationFail(
    { dispatch }: StateContext<StateModel<INotification>>,
    { payload }: fromNotificationsAction.SaveNotificationFail
  ): Observable<never> {
    dispatch(new SetFormPristine('notifications.selectedModel'));

    return throwError(() => new Error(payload.message));
  }
}

